// Copyright aptihealth, inc. 2019 All Rights Reserved
import React, { Component } from "react";
import "./styles.scss";
import { api } from "../../../APIRequests";
import { getPatientProfile, isAuthorized, isUserPrescriber } from "../../../redux/actions/auth";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import AddMessageChannel from "./AddMessageChannel";
import Spinner from "../../UI/Spinner";
import images from "../../../utils/images";
import moment from "moment-timezone";
import { getQueryParams } from "../../../utils/filters";
import { MessagesContainer } from "./MessagesContainer";
import { SUPPORT_NUMBER, SUPPORT_NUMBER_HREF } from "../../../constants/aptihealth";

class MessagesView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      channels: [],
      nextChannelPageQuery: null,
      loadingMoreChannels: false,
      isMobile: window.innerWidth <= 640,
      mobileView: "CHANNEL_SELECT",
      initialLoadComplete: false,
      isInactive: false,
      sendbirdConnectionIssue: false,
    };
  }

  async componentDidMount() {
    const { getPatientProfile } = this.props;

    if (isAuthorized("user:patient")) {
      await getPatientProfile();
    }

    if (
      isAuthorized("user:patient") &&
      this.props.profile !== undefined &&
      this.props.profile.status === "INACTIVE"
    ) {
      this.setState({ isInactive: true });
    }
    await this.fetchMessagingInfo();
    await this.processChannels();
    window.addEventListener("resize", this.handleResize);
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.profile === undefined &&
      this.props.profile !== undefined &&
      this.props.profile.status === "INACTIVE"
    ) {
      this.setState({ isInactive: true });
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.handleResize);
    this.state.client.removeAllChannelHandlers();
    this.state.client.disconnect();
  }

  fetchMessagingInfo = async () => {
    const options = { params: { showLoader: false } };
    let user = await api.messaging.fetch_user({ options });
    let contacts = await api.messaging.fetch_contacts({ options });
    let client = api.messaging.create_client();

    try {
      await api.messaging.connect_to_sendbird(client, user.user_id, user.access_token);
    } catch (err) {
      console.error("Sendbird connection issue:", err);
      this.setState({ sendbirdConnectionIssue: true });
      throw err;
    }

    const { channels, nextChannelPageQuery } = await api.messaging.get_paginated_channels(client);
    const processedChannels = channels.map((channel) => this.processChannel(user, channel));

    const ChannelHandler = new client.ChannelHandler();

    ChannelHandler.onUserMuted = (channel) => {
      this.processUpdatedChannel(channel);
    };
    ChannelHandler.onUserUnmuted = (channel) => {
      this.processUpdatedChannel(channel);
    };
    client.addChannelHandler("MESSAGE_VIEW_INDEX_HANDLER", ChannelHandler);

    this.setState({
      client,
      user,
      contacts,
      channels: processedChannels,
      nextChannelPageQuery,
    });

    return user;
  };

  processChannels = async () => {
    let selectedChannel, isAddingChannel;
    const memberId = getQueryParams("memberId", this.props.location.search);
    let foundChannel;
    const channels = this.state.channels;

    if (memberId) {
      foundChannel = channels.find(
        (channel) =>
          channel.otherMembersMap &&
          Object.keys(channel.otherMembersMap).length === 1 &&
          Object.hasOwn(channel.otherMembersMap, memberId),
      );
    }

    if (memberId && !foundChannel) {
      await this.handleDirectMessageSelection({ user_id: memberId });
      this.setState({
        initialLoadComplete: true,
      });

      return;
    } else if (foundChannel) {
      selectedChannel = foundChannel;
      isAddingChannel = false;
    } else if (channels.length > 0) {
      selectedChannel = channels[0];
      isAddingChannel = false;
    } else {
      selectedChannel = null;
      isAddingChannel = true;
    }

    this.setState({
      selectedChannel,
      isAddingChannel,
      initialLoadComplete: true,
    });
  };

  handleResize = () => {
    this.setState((state) => {
      let isMobile = window.innerWidth <= 640;

      if (state.isMobile !== isMobile) {
        return {
          isMobile,
          mobileView: "CHANNEL_SELECT",
        };
      }
    });
  };

  processChannel = (user, channel) => {
    channel.otherMembers = channel.members.filter((member) => member.userId !== user.user_id);

    channel.otherMembersMap = channel.otherMembers.reduce((map, member) => {
      map[member.userId] = member;

      return map;
    }, {});

    if (channel.otherMembers.length === 1) {
      let otherMember = channel.otherMembers[0];

      channel.coverImage =
        otherMember.profileUrl !== "" ? otherMember.profileUrl : images("./common/avatar.png");
    }

    if (channel.lastMessage) {
      let createdAt = moment(channel.lastMessage.createdAt);
      let format = createdAt.isSame(moment(), "day") ? "hh:mm A" : "MM/DD/YY hh:mm A";

      channel.lastMessage.formattedTime = createdAt.format(format);
    }

    return channel;
  };

  showMoreChannels = async () => {
    const { nextChannelPageQuery, user } = this.state;

    this.setState({ loadingMoreChannels: true });

    const { channels: newChannels, nextChannelPageQuery: newNextChannelPageQuery } =
      await api.messaging.get_next_channel_page(nextChannelPageQuery);
    const processedChannels = newChannels.map((channel) => this.processChannel(user, channel));

    this.setState({
      channels: [...this.state.channels, ...processedChannels],
      nextChannelPageQuery: newNextChannelPageQuery,
      loadingMoreChannels: false,
    });
  };

  switchMessagesOrAddChannelView = () => {
    this.setState((state) => {
      // If there are no channels do not allow to leave addChannelView
      let isAddingChannel = this.state.channels.length === 0 ? true : !state.isAddingChannel;

      return {
        isAddingChannel,
      };
    });
  };

  mutePatientIfNeeded = async (processedChannel, patientId) => {
    if (!isUserPrescriber()) {
      return;
    }

    const urlParams = { patientId };
    const data = {
      patient_read_only_message_thread: true,
      channel_url: processedChannel.url,
    };

    await api.provider.toggle_patient_read_only_message_thread({
      data,
      urlParams,
    });
  };

  handleDirectMessageSelection = async (contact) => {
    let userIds = [this.state.user.user_id, contact.user_id];
    let upsertChannel = await api.messaging.upsert_distinct_channel(this.state.client, userIds);
    let channels = Array.from(this.state.channels);

    // Put selected channel at the top
    let existingChannelIndex = channels.findIndex(
      (channel) => channel.url === upsertChannel.channel.url,
    );

    if (upsertChannel.isCreated || existingChannelIndex === -1) {
      await this.mutePatientIfNeeded(upsertChannel.channel, contact.user_id);
      let newChannel = this.processChannel(this.state.user, upsertChannel.channel);

      channels.unshift(newChannel);
    } else {
      let processedExistingChannel = this.processChannel(this.state.user, upsertChannel.channel);

      channels.splice(existingChannelIndex, 1);
      channels.unshift(processedExistingChannel);
    }

    this.setState({
      selectedChannel: upsertChannel.channel,
      channels,
      isAddingChannel: false,
      mobileView: "CHAT",
    });
  };

  onSelectChannel = (channel) => {
    this.setState({
      selectedChannel: channel,
      mobileView: "CHAT",
    });
  };

  processUpdatedChannel = (updatedChannel) => {
    const nonUpdatedChannels = Array.from(this.state.channels).filter(
      (channel) => channel.url !== updatedChannel.url,
    );
    const processedUpdatedChannel = this.processChannel(this.state.user, updatedChannel);

    this.setState({
      channels: [processedUpdatedChannel, ...nonUpdatedChannels],
    });
  };

  renderSendbirdUnavailableError = () => {
    return (
      <div className="d-flex text-center tw:justify-center">
        <div className="my-5 mx-2" style={{ maxWidth: 580 }}>
          <p className="fs-26 my-3 tw:font-bold">aptihealth messaging is currently unavailable.</p>
          <p className="fs-18">
            We apologize for the inconvenience. If you need to reach someone, contact support at{" "}
            <a href={SUPPORT_NUMBER_HREF} className="Messages--error-link">
              {SUPPORT_NUMBER}
            </a>{" "}
            or{" "}
            <a href="mailto:support@aptihealth.com" className="Messages--error-link">
              support@aptihealth.com
            </a>
            .
          </p>
          <br />
          <p className="fs-18">
            Go to the{" "}
            <a href="/app/home" target="_self" className="Messages--error-link">
              dashboard
            </a>
            .
          </p>
        </div>
      </div>
    );
  };

  render() {
    if (this.state.sendbirdConnectionIssue) {
      return this.renderSendbirdUnavailableError();
    }

    if (!this.state.initialLoadComplete) {
      return <Spinner />;
    }

    if (this.state.isAddingChannel) {
      return (
        <AddMessageChannel
          contacts={this.state.contacts}
          channels={this.state.channels}
          switchView={this.switchMessagesOrAddChannelView}
          handleSelect={this.handleDirectMessageSelection}
        />
      );
    }

    return (
      <MessagesContainer
        channels={this.state.channels}
        client={this.state.client}
        fetchMessagingInfo={this.fetchMessagingInfo}
        isInactive={this.state.isInactive}
        isMobile={this.state.isMobile}
        loadingMoreChannels={this.state.loadingMoreChannels}
        mobileView={this.state.mobileView}
        nextChannelPageQuery={this.state.nextChannelPageQuery}
        onSelectChannel={this.onSelectChannel}
        selectedChannel={this.state.selectedChannel}
        showMoreChannels={this.showMoreChannels}
        switchMessagesOrAddChannelView={this.switchMessagesOrAddChannelView}
        user={this.state.user}
        switchMobileView={() => this.setState({ mobileView: "CHANNEL_SELECT" })}
        onMessageReceived={this.processUpdatedChannel}
      />
    );
  }
}
const mapStateToProps = (state) => {
  return {
    profile: state.auth.profile,
    flags: state.flags,
  };
};

export default connect(mapStateToProps, {
  getPatientProfile,
})(withRouter(MessagesView));
